import { createSelector } from 'reselect';
import { selectSubscriptions, selectSignedInUserInfo, selectSelectedSubscription, selectSubscribedMsiVessels } from '../user-management/appSelectors';
import { IStatsFormData, ISubscriptionFormData, IUserInfoFormData, IVesselFormData } from './sharedTypes';

import { size, uniq, map, uniqBy } from 'lodash';
import { selectSelectionData } from '../features/search/searchSelectors';
import { RootState } from '../core/store';
import { TSectorColourInfo } from './containers/left-sidebar/leftSidebarTypes';
import { ISubscriptionState } from '../user-management/appSlice';
import { TSubscribedMsiVessel } from '../user-management/subscription/subscriptionTypes';
import { ESubscriptionType } from './sharedEnums';

const selectVesselsLastSignalSlice = (state: RootState) => state.vesselsLastSignalSlice;
const selectSearchSlice = (state: RootState) => state.searchSlice;


export const selectIsUserSubscribedToHorizon = createSelector(
    [selectSubscriptions],
    (subscriptions) =>
        subscriptions.filter(k => k.type !== ESubscriptionType.Seascape).length > 0 as boolean
)

export const selectUserSubscriptionsFormData = createSelector(
    [selectSubscriptions],
    (subscriptions) =>
        subscriptions.filter(k => k.type === ESubscriptionType.Seascape).map(s => ({
            id: s.id,
            name: s.name,
            seascapeActivityLevel: s.seascapeActivityLevel,
            subscribedSectorIdList: s.sectors.map(s => s.sectorId)
        })) as ISubscriptionFormData[]

)

export const selectSelectedSubscriptionFormData = createSelector(
    [selectSelectedSubscription],
    (selectedSubscription) => ({
        id: selectedSubscription.id,
        name: selectedSubscription.name,
        seascapeActivityLevel: selectedSubscription.seascapeActivityLevel,
        subscribedSectorIdList: selectedSubscription.sectors.map(s => s.sectorId)
    }) as ISubscriptionFormData
);

export const selectUserInfoFormData = createSelector([selectSignedInUserInfo], (signedInUserInfo) => {
    if (!signedInUserInfo)
        return null;

    const initials = signedInUserInfo.fullName.replace(/[^a-zA-Z- ]/g, '').match(/\b\w/g) || '';
    const { fullName, emailAddress, clientName } = signedInUserInfo;
    return { initials, fullName, emailAddress, clientName } as IUserInfoFormData;
});

export const selectSubscribedVesselsFormData = createSelector([selectSubscribedMsiVessels],
    (subscribedMsiVessels) =>
        subscribedMsiVessels.map(v => ({ name: v.vesselName, imoNumber: v.imoNumber, sectorName: v.sectorName, companyName: v.owner })) as IVesselFormData[]
);

export const selectOutputStats = createSelector([selectSelectionData],
    (selectionData) => {
        return ({
            vesselCount: selectionData.selectedVesselsList.length,
            ownershipCount: size(uniq(map(selectionData.selectedVesselsList, 'companyName'))),
            sectorCount: size(uniq(map(selectionData.selectedVesselsList, 'sectorName')))
        } as IStatsFormData)

    });

export const selectSectorColours = createSelector(
    selectVesselsLastSignalSlice, selectSearchSlice,
    (vesselsLastSignalSlice, searchSlice) => {
        let filtered = vesselsLastSignalSlice.vesselsLastSignalData?.features;
        if (searchSlice.selectedVesselsList && searchSlice.selectedVesselsList.length > 0 && filtered && filtered.length > 0) {
            const selectedImoNumbers = new Set(searchSlice.selectedVesselsList.map(v => v.imoNumber));
            filtered = filtered.filter((f) => f.properties?.imoNumber !== undefined && selectedImoNumbers.has(f.properties.imoNumber));
            filtered.sort((a, b) => {
                const sectorA = a.properties?.order || 0;
                const sectorB = b.properties?.order || 0;
                if (sectorA < sectorB) return -1;
                if (sectorA > sectorB) return 1;
                return 0;
            });
        }

        return uniqBy(filtered, f => f.properties?.sectorName).map((f) => {
            return { sectorName: f.properties?.sectorName, colour: f.properties?.color }
        }) as TSectorColourInfo[]
    }
);

const selectSubscriptionData = createSelector(
    [(state: RootState) => state.appSlice],
    (appSlice) => appSlice.subscriptionData as ISubscriptionState
);

const vesselsLastSignalSlice = (state: RootState) => state.vesselsLastSignalSlice;

export const selectSubscribedMsiVesselByImoNumber = createSelector(
    [selectSubscriptionData, vesselsLastSignalSlice, (_state, imoNumber: number) => imoNumber],
    (subscriptionData, vesselsLastSignalSlice, imoNumber) => {
        const vesselData = { ...subscriptionData.subscribedMsiVessels.find(v => v.imoNumber === imoNumber) };
        const vesselLastSignal = vesselsLastSignalSlice.vesselsLastSignalData?.features.find(v => v.properties?.imoNumber === imoNumber);

        vesselData.fuelSystem = vesselData.sectorCode === 'LNG' ? vesselData.propulsionSystem : vesselData.fuelSystem ? vesselData.fuelSystem : 'Conventional';

        if (vesselData && vesselLastSignal) {
            vesselData.reportedEta = vesselLastSignal?.properties?.reportedEta;
            vesselData.reportedDestination = vesselLastSignal?.properties?.reportedDestination;
        }

        return vesselData as TSubscribedMsiVessel;
    }
);

export const selectVesselSizeUnit = createSelector(
    [selectSubscriptionData, (_state, imoNumber: number) => imoNumber],
    (subscriptionData, imoNumber) => {
        const vesselData = { ...subscriptionData.subscribedMsiVessels.find(v => v.imoNumber === imoNumber) };

        return vesselData.sizeUnit
    }
);

// selector to get selected vessels' IMO numbers
export const selectSelectedVesselsImoNumbers = createSelector(
    [selectSelectionData],
    (selectionData) => {
        return selectionData.selectedVesselsList.map(v => v.imoNumber);
    }
);

export const selectSelectedVesselsSectors = createSelector(
    [selectSubscribedMsiVessels, selectSelectionData],
    (subscribedMsiVessels, selectionData) => {
        if (!subscribedMsiVessels || subscribedMsiVessels.length === 0) {
            return [];
        }
        const selectedImoNumbers = new Set(selectionData.selectedVesselsList.map(v => v.imoNumber));
        const distinctSectorCodes = Array.from(new Set(
            subscribedMsiVessels
                .filter(v => selectedImoNumbers.has(v.imoNumber))
                .map(v => v.sectorCode)
        )
        );
        return distinctSectorCodes;
    }
);